import { css, useTheme } from "@emotion/react"
import React, { useState } from "react"
import GridContainer from "./GridContainer"
import GridCol from "./GridCol"
import { Link } from "@gatsbyjs/reach-router"
import { colorsDark, colorsLight, focusStyles, fontSizeLineHeight, mediaQueries, pxToRem, transition, z } from "../styles"
import {
  Neutral0,
  Neutral1000,
  Neutral200,
  Neutral400,
  Neutral600,
  Violet300,
  Violet400,
} from "../../tokens/js/colors"

import logoOnlyH from "../logos/upsun-only-h.svg"
import formerlyPsh from "../logos/formerly-psh-h.svg"

import { UpsunTheme } from "../themes"
import Button from "./Button"

import { ReactComponent as GithubLogo } from "../icons/github.svg"
import { ReactComponent as RedditLogo } from "../icons/reddit.svg"
import { ReactComponent as BlueskyLogo } from "../icons/bluesky.svg"
import { ReactComponent as XLogo } from "../icons/x.svg"
import { ReactComponent as YoutubeLogo } from "../icons/youtube.svg"
import { ReactComponent as LinkedinLogo } from "../icons/linkedin.svg"

import { ReactComponent as BCorp } from "../icons/bcorp.svg"

import {
  NeutralBgDefault,
  NeutralBgWeakDisabled,
  NeutralIconWeakDefault,
  NeutralTxtWeakDefault,
  PrimaryTxtWeakHover,
} from "../../tokens/js/light"
import {
  NeutralBgWeakDisabled as NeutralBgWeakDisabledDark,
  NeutralBgDefault as NeutralBgDefaultDark,
  NeutralIconWeakDefault as NeutralIconWeakDefaultDark,
  NeutralTxtWeakDefault as NeutralTxtWeakDefaultDark,
  PrimaryTxtWeakHover as PrimaryTxtWeakHoverDark,
} from "../../tokens/js/dark"

import { ReactComponent as Moon } from "../icons/moon.svg"
import { ReactComponent as Sun } from "../icons/sun.svg"
import { ReactComponent as SuccessIcon } from "../icons/success.svg"
import { ReactComponent as DiscordIcon } from "../icons/discord.svg"
import { useForm } from "react-hook-form";

type FooterLink = {
  text: string
  href: string
}

type FooterCategory = {
  title: string,
  links: FooterLink[]
}

type FooterData = {
  categories: FooterCategory[]
  bottomLinks: FooterLink[]
  homepage?: string,
  allRightsReserved?: string
}

type FooterProps = {
  changeTheme(): void
  handleSubmit(email: string): void
  data : FooterData
  simpleFooter?: boolean
}

const colors = {
  black200: Neutral200,
  black400: Neutral400,
  black600: Neutral600,
  violet300: Violet300,
  violet400: Violet400,
  borderDefault: Neutral200,
}

const Footer: React.FC<FooterProps> = ({ 
  data, 
  simpleFooter, 
  changeTheme, 
  handleSubmit
}) => {
  const theme = useTheme() as UpsunTheme
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const footerWrapperStyles = css`
    margin-bottom: ${pxToRem(32)};
    font-family: "Space Grotesk";

    a,
    span {
      color: ${theme.name === "light" ? Neutral1000 : Neutral0};
      background: none;
      transition: none;
    }
  `

  const logoStyles = css`
    display: flex;
    flex-direction: row;
    align-items: baseline;
    gap: ${pxToRem(8)};
    margin-bottom: 48px;

    a {
      position: relative;
      height: ${pxToRem(32)};

      &:focus-visible {
        ${focusStyles}
      }
    }

    path {
      fill: ${theme.name === "light" ? Neutral1000 : Neutral0};
    }

    ${mediaQueries.md} {
      a {
        height: 52px;
      }
    }

    ${mediaQueries.lg} {
      margin-bottom: ${pxToRem(48)};
    }
  `

  const rule = css`
    height: ${pxToRem(1)};
    background-color: ${colors.black600};
    margin-top: ${pxToRem(24)};
    margin-bottom: ${pxToRem(24)};
  `

  const linkStyles = css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: ${pxToRem(24)};
    margin-bottom: ${pxToRem(32)};
    font-size: 13px;
    color: #8b9397;
    text-align: center;

    a {
      text-decoration: none;
      transition: ${transition()};

      &:hover,
      &:active,
      &:focus-visible {
        color: ${theme.name === "light"
          ? PrimaryTxtWeakHover
          : PrimaryTxtWeakHoverDark};
      }
    }

    span {
      color: ${theme.name === "light"
        ? NeutralTxtWeakDefault
        : NeutralTxtWeakDefaultDark};
    }

    div {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
      gap: ${pxToRem(24)};
    }

    ${mediaQueries.md} {
      justify-content: flex-start;

      div {
        justify-content: flex-start;
      }
    }

    ${mediaQueries.lg} {
      span {
        margin-right: auto;
      }
    }
  `

  const firstRowStyles = css`
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;

    ${mediaQueries.md} {
      margin-bottom: ${pxToRem(24)};
      justify-content: space-between;

      #footer-logo {
        order: 1;
      }

      #footer-community {
        order: 2;
      }

      #footer-links {
        order: 3;
      }

      #footer-newsletter {
        order: 4;
      }

      #footer-social {
        order: 5;
      }
    }

    ${mediaQueries.xl} {
      #footer-logo {
        order: 1;
      }

      #footer-links {
        order: 2;
      }
      
      #footer-community {
        order: 3;
      }

      #footer-newsletter {
        order: 4;
      }

      #footer-social {
        order: 5;
      }
    }
  `

  const communityBtnContainer = css`
    display: flex;
    justify-content: center;
    flex: 0 0 100%;

    a {
      padding: 12px 32px;
      font-size: ${pxToRem(16)};
      flex: 0 0 100%;
    }

    a:hover svg path {
      fill: ${theme.name === "light" ? Neutral0 : Neutral1000};
    }

    ${mediaQueries.md} {
      margin: 0;
      justify-content: flex-end;

      flex: 0 0 auto;

      a {
        flex: 0 0 auto;
      }
    }
  `

  const socialIconsContainer = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 100%;
    gap: 36px;
    margin-top: ${simpleFooter ? pxToRem(22) : pxToRem(40)};

    svg {
      margin-bottom: 0;
    }

    ${mediaQueries.md} {
      flex-direction: row;
      justify-content: space-between;
    }
  `

  const linksRow = css`
    display: flex;
    flex-direction: column;
    margin-bottom: 48px;
    font-size: 16px;

    a {
      text-decoration: none;

      transition: ${transition()};

      &:hover,
      &:active,
      &:focus-visible {
        color: ${theme.name === "light"
          ? PrimaryTxtWeakHover
          : PrimaryTxtWeakHoverDark};
      }
    }

    ${mediaQueries.md} {
      margin-bottom: 0;
    }
  `

  const cookieSettings = css`
    cursor: pointer;
  `

  const discordIconStyles = css`
    position: relative;
    top: 2px;
    margin-right: 8px;

    path {
      fill: ${theme.name === "light" ? Neutral1000 : Neutral0};
    }
  `

  const currentYear = new Date().getFullYear()

  const themeToggleStyles = css`
    background: ${theme.name === "light"
      ? NeutralBgWeakDisabled
      : NeutralBgWeakDisabledDark};
    border: none;
    cursor: pointer;
    border-radius: 32px;
    width: 72px;
    height: 32px;
    position: relative;
    top: -5px;
    left: 0;

    &::after {
      display: block;
      content: "";
      width: 32px;
      height: 24px;
      border-radius: 40px;
      background-color: ${theme.name === "light"
        ? NeutralBgDefault
        : NeutralBgDefaultDark};
      position: absolute;
      top: 4px;
      right: ${theme.name === "light" ? "calc(100% - 32px - 4px)" : "4px"};
      transition: all 0.25s ease-in-out;
    }
  `

  const sunStyles = css`
    position: relative;
    top: 2px;
    right: 8px;
    z-index: ${z("over-main")};

    path {
      fill: ${theme.name === "light"
        ? NeutralIconWeakDefault
        : NeutralIconWeakDefaultDark};
    }
  `

  const moonStyles = css`
    position: relative;
    z-index: ${z("over-main")};
    top: 2px;
    left: 8px;

    path {
      fill: ${theme.name === "light"
        ? NeutralIconWeakDefault
        : NeutralIconWeakDefaultDark};
    }
  `

  const toggleContainer = css`
    position: relative;
    cursor: pointer;
  `

  const upsunLogoStyles = css`
    display: flex;

    a {
      margin-top: 15px;

      ${mediaQueries.md} {
        position: relative;
        top: 8px;
        height: 52px;
      }

      ${mediaQueries.md} {
        top: 12px;
      }

      &:focus-visible {
        ${focusStyles}
      }
    }

    img {
      height: ${pxToRem(38)};
      width: auto;
      filter: invert(${theme.name === "light" ? 0 : 1});

      ${mediaQueries.md} {
        height: ${pxToRem(24)};
      }
  
      ${mediaQueries.xl} {
        height: ${pxToRem(32)};
      }
    }
  `

  const bcorpStyles = css`
    filter: invert(${theme.name === "light" ? 0 : 1});
  `

  const socialIcons = css`
    display: flex;
    gap: 24px;
    align-items: flex-end;
  `;

  const categoryRowsContainer = css`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    flex-wrap: wrap;
    align-items: flex-start;

    div {
      flex: 0 0 50%;
    }

    ${mediaQueries.md} {
      row-gap: ${pxToRem(24)};
      flex: 0 0 100%;

      div {
        flex: 0 0 33%;
      }
    }

    ${mediaQueries.xl} {
      flex: 0 0 60%;
    }
  `;

  const newsletterStyles = css`
    display: flex;
    flex-direction: column;
    width: 100%;

    margin-top: ${pxToRem(48)};
    margin-bottom: ${pxToRem(48)};

    gap: ${pxToRem(12)};

    ${mediaQueries.md} {
      width: 50%;
    }

    ${mediaQueries.xl} {
      flex-direction: row;
      align-items: center;

      position: relative;
      left: 25%;
    }

    span {
      flex: 0 0 auto;
    }
  `;

  const newsletterInputContainer = css`
    flex-grow: 1;
    form {
      display: ${hasSubmitted ? "none" : "flex"};
      flex-direction: column;
      width: 100%;
      gap: ${pxToRem(12)};

      ${mediaQueries.md} {
        flex-direction: row;
        align-items: center;

        input[type="email"] {
          height: ${pxToRem(44)};
        }

        input[type="email"] {
          height: ${pxToRem(52)};
        }

        input[type="submit"] {
          max-width: ${pxToRem(130)};
        }
      }

      input[type="email"] {
        flex-grow: 1;
      }
    }
  `;

  const thankYouMessage = css`
    display: ${hasSubmitted ? "flex" : "none"};
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: ${pxToRem(12)};

    ${fontSizeLineHeight(16,24)};
    padding: ${pxToRem(12)};
    border-radius: ${pxToRem(8)};
    color: ${theme.name === "light" ? colorsLight.NeutralTxtWeakDefault : colorsDark.NeutralTxtWeakDefault};
    background-color: ${theme.name === "light" ? colorsLight.NeutralBgDefault : colorsDark.NeutralBgDefault};
    border: 2px solid ${theme.name === "light" ? colorsLight.NeutralBorderDisabled : colorsDark.NeutralBorderDisabled};

    ${mediaQueries.md} {
      width: 72%;
    }
  `;

  const { register, handleSubmit : handleSubmitForm, setValue } = useForm({
    shouldUseNativeValidation: true
  });

  const homepage = data?.homepage || "/";
  const allRightsReserved = data?.allRightsReserved || "All rights reserved.";

  return (
    <GridContainer>
      <GridCol colStart={1} colEnd={13}>
        <div css={footerWrapperStyles}>
          <div css={firstRowStyles}>
            <div css={upsunLogoStyles} id="footer-logo">
              <Link to={homepage} className="logoLink" title="Upsun" css={logoStyles}>
                <img src={logoOnlyH} alt="" />
              </Link>
              <Link to="/platform-sh-is-now-upsun/" title="Formerly Platform.sh" css={logoStyles}>
                <img src={formerlyPsh} alt="" />
              </Link>
            </div>

            {!simpleFooter && <>
              <div css={categoryRowsContainer} id="footer-links">
              {
                data.categories.map(category => <div css={linksRow} key={category.title}>
                  <strong>{category.title}</strong>
                  {
                    category.links.map(link => {
                      const isExternalLink = link.href?.substring(0, 4) === "http";

                      if(isExternalLink) {
                        return <a href={link.href} key={`${category.title}-${link.text}`}>{link.text}</a>
                      } else {
                        return <Link to={link.href} key={`${category.title}-${link.text}`}>{link.text}</Link>
                      }
                    })
                  }
                </div>)
              }
              </div>
              <div css={newsletterStyles} id="footer-newsletter">
                <span><strong>Join our monthly newsletter</strong></span>
                <div css={newsletterInputContainer}>
                  <div css={thankYouMessage}>
                    <div>Thank you for subscribing!</div>
                    <SuccessIcon />
                  </div>
                  <form onSubmit={handleSubmitForm((data: any) => {
                    handleSubmit(data.email);
                    setHasSubmitted(true);
                  })}>
                    <input type="email" placeholder="" {...register("email", { required: true })} />
                    <Button level="primary" type="submit">Subscribe</Button>
                  </form>
                </div>
              </div>
            </>}

            <div css={communityBtnContainer} id="footer-community">
              <Button level="secondary" href="https://discord.gg/upsun">
                <DiscordIcon css={discordIconStyles} />
                Discord
              </Button>
            </div>
            <div css={socialIconsContainer} id="footer-social">
              <div>
                <BCorp css={bcorpStyles} />
              </div>
              <div css={socialIcons}>
                <a
                  href="https://github.com/upsun"
                  title="Upsun's github account"
                >
                  <GithubLogo css={logoStyles} />
                </a>
                <a
                  href="https://www.reddit.com/r/upsun/ "
                  title="Upsun's Reddit account"
                >
                  <RedditLogo css={logoStyles} />
                </a>
                <a
                  href="https://bsky.app/profile/upsun.com"
                  title="Upsun's Bluesky account"
                >
                  <BlueskyLogo css={logoStyles} />
                </a>
                <a
                  href="https://www.youtube.com/@upsundotcom"
                  title="Upsun's youtube account"
                >
                  <YoutubeLogo css={logoStyles} />
                </a>
                <a href="https://x.com/upsundotcom" title="Upsun's X account">
                  <XLogo css={logoStyles} />
                </a>
                <a
                  href="https://www.linkedin.com/company/upsundotcom"
                  title="Upsun's linkedin account"
                >
                  <LinkedinLogo css={logoStyles} />
                </a>
              </div>
            </div>
          </div>
          <div css={rule}></div>
          <div css={linkStyles}>
            <span>
              <a href={homepage}>© {currentYear} Upsun. {allRightsReserved}</a>
            </span>
            <div>
              {
                data.bottomLinks.map(link => <a href={link.href} key={`bottomLink-${link.text}`}>{link.text}</a>)
              }
              <a className="ot-sdk-show-settings" css={cookieSettings}>
                Manage your cookie preferences
              </a>
            </div>
            <div css={toggleContainer}>
              <button
                title="Toggle light and dark theme"
                css={themeToggleStyles}
                onClick={changeTheme}
              >
                <Sun css={sunStyles} />
                <Moon css={moonStyles} />
              </button>
            </div>
          </div>
        </div>
      </GridCol>
    </GridContainer>
  )
}

export default Footer
